import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
// Service
import { BroadcastService, ToasterType } from '@cv-invision/core';
import { ExternalApiService } from '../../../../../services/external-api.service';
import { ViewsService } from '../../../../../../services/views.service';
import { LayoutRenderService } from '../../../../services/layout-render.service';
// Model
import { LayoutComponentBroadcast, LayoutRenderLayoutItem } from '../../../../models/cv-layout-render.model';
import { GeneralEventType } from '../../../models/component.events';

@Component({
	selector: 'cv-invision-ux-general-order-data',
	templateUrl: './general-order-data.component.html',
	styleUrls: ['./general-order-data.component.scss'],
})
export class GeneralOrderDataComponent implements OnInit, OnDestroy {
	@Input() data: any;
	@Input() layoutNode = new LayoutRenderLayoutItem();

	path = null;
	isSavingInProcess = false;
	formControlNamesPassthrough = [];

	get isEditing(): boolean {
		return this.layoutService.activateEditableUX;
	}

	get isPristine(): boolean {
		return (this.viewsService.form as FormGroup).pristine;
	}

	get viewGseFields(): boolean {
		return this.layoutService.extraData.permissions.viewGseFields;
	}

	get availableLenders(): any {
		return this.viewsService.lendersAvailables?.data || [];
	}

	get fungibleStatus(): string {
		let txt = 'deactivated';

		if (this.data.order.gseFungible) {
			txt = 'active'
		}

		return txt;
	}

	get hasAvailableLenders(): boolean {
		return this.data.order.lenderSelectionEnable;
	}

	protected atComponentDestroy = new Subject();

	constructor(
		protected broadcast: BroadcastService,
		protected viewsService: ViewsService,
		protected externalApi: ExternalApiService,
		protected layoutService: LayoutRenderService
	) {
		//
	}

	ngOnDestroy(): void {
		this.atComponentDestroy.complete();
		this.atComponentDestroy.unsubscribe();

	}

	ngOnInit(): void {
		this.path = this.data._Path;
	}

	/**
	 * Get the changes and save the to the API
	 */
	async saveChanges(showToaster = true): Promise<void> {
		// Implement promise with await, the right way
		//
		// return new Promise((resolve, reject) => {
        //     (async(textureFileScope, polygonScope, responseScope) => {
		// 	})(textureFile, polygon, response);
		// });
		return new Promise(async (resolve, reject) => {
			this.isSavingInProcess = true;

			let eventData = {
				type: 'danger',
				title: 'API ERROR',
				message: ''
			};

			const dirtyObj = {};
			const updatedPaths = {};
			this.getUpdates(this.viewsService.form, dirtyObj, updatedPaths);

			const updates = [];
			for (const path in updatedPaths) {
				updates.push({
					path,
					value: updatedPaths[path]
				});
			}

			if (updates.length) {
				const mediaResponse = await this.externalApi.patchDocument(this.viewsService.order.id, updates, this.viewsService.etag).toPromise();

				if (mediaResponse.body.succeed) {
					eventData = {
						type: 'success',
						title: 'Data Updated',
						message: ''
					};

					this.layoutService.componentBroadcast.next({
						eventType: GeneralEventType.UPDATE_DATA,
						jsonPath: this.path
					});

				} else {
					eventData.message = mediaResponse.body.message;
				}

				if (showToaster) {
					this.broadcast.broadcast({
						name: ToasterType.TOASTER,
						event: eventData
					});
				}

			}

			this.isSavingInProcess = false;
			resolve();
		})
	}

	/**
	 * Recursive function that genereates the Dirty-only object & the updated full path object.
	 * After getting the "field", the function mark the field as pristine.
	 *
	 * @param formItem FormGroup | FormArray | FormControl
	 * @param updatedValuesObject Any writable object {}
	 * @param updatedPaths Any writable object {}
	 * @param name formcontrolName
	 * @param trailingPath Path of the read object
	 * @param passthrough Flag to tell the function to print the object instead of deep read it
	 */
	private getUpdates(
		formItem: FormGroup | FormArray | FormControl,
		updatedValuesObject: any,
		updatedPaths: any,
		name?: string,
		trailingPath?: string,
		passthrough?: boolean
	) {
		if (formItem instanceof FormControl || passthrough) {
			if ((name && formItem.dirty) || passthrough) {
				updatedValuesObject[name] = formItem.value;
				updatedPaths[`${trailingPath}${name}`] = formItem.value;
				formItem.markAsPristine();
			}
		} else {
			for (const formControlName in formItem.controls) {
				if (Object.prototype.hasOwnProperty.call(formItem.controls, formControlName)) {
					const formControl = formItem.controls[formControlName];
					let nPath = '';
					if (trailingPath) {
						nPath = `${trailingPath}.`;
					}

					if (formControl instanceof FormControl) {
						this.getUpdates(formControl, updatedValuesObject, updatedPaths, formControlName, nPath);

					} else if (
						formControl instanceof FormArray &&
						formControl.dirty &&
						formControl.controls.length > 0
					) {
						let pass = false;
						if (this.formControlNamesPassthrough.indexOf(formControlName) > -1) {
							pass = true;
						} else {
							nPath = `${nPath}${formControlName}`;
						}

						updatedValuesObject[formControlName] = [];
						this.getUpdates(formControl, updatedValuesObject[formControlName], updatedPaths, formControlName, nPath, pass);

					} else if (formControl instanceof FormGroup && formControl.dirty) {
						if (isNaN(parseInt(formControlName))) {
							nPath = `${nPath}${formControlName}`;
						} else {
							nPath = `${nPath}[${formControlName}]`;
							nPath = nPath.replace('.[', '[');
						}
						updatedValuesObject[formControlName] = {};
						this.getUpdates(formControl, updatedValuesObject[formControlName], updatedPaths, null, nPath);
					}
				}
			}
		}
	}
}
